import { mapGetters } from 'vuex';
import Forms from './../../utils/forms/forms';
import chartOfAccountsService from './../../services/chartOfAccounts';
import organizationSettingsService from './../../services/organizationSettings';
import organizationSettingsTransformer from './../../transformers/organizationSettings';
import store from './../../store';

export default {
    data() {
        return {
            sortField: 'code',
            sortDirection: 'asc',
            savingOrgDefault: false,
            form: new Forms({
                code: {
                    value: '',
                    type: 'text',
                },
                name: {
                    value: '',
                    type: 'text',
                },
                description: {
                    value: '',
                    type: 'text',
                },
            }),
            ready: false,
        };
    },

    props: {
    },

    computed: {
        ...mapGetters({
            chartOfAccounts: 'allChartOfAccounts',
            organizationSettings: 'organizationSettings',
        }),
        /** Array of chart of accounts for template; getter returns { all: [] }. */
        coaList() {
            const all = this.chartOfAccounts && this.chartOfAccounts.all;
            return Array.isArray(all) ? all : [];
        },
        sortedCoaList() {
            const list = this.coaList.slice();
            const key = this.sortField;
            const dir = this.sortDirection === 'asc' ? 1 : -1;
            list.sort((a, b) => {
                const va = (a[key] != null ? String(a[key]) : '').toLowerCase();
                const vb = (b[key] != null ? String(b[key]) : '').toLowerCase();
                if (va < vb) return -1 * dir;
                if (va > vb) return 1 * dir;
                return 0;
            });
            return list;
        },
    },

    methods: {
        onSort(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDirection = 'asc';
            }
        },

        orgDefaultRoleFor(item) {
            const s = this.organizationSettings;
            if (!s || !item) return '';
            if (s.receivablesAccountDefaultId === item.id) return 'receivables';
            if (s.incomeAccountDefaultId === item.id) return 'income';
            if (s.vatAccountDefaultId === item.id) return 'vat';
            if (s.depositAccountDefaultId === item.id) return 'deposit';
            return '';
        },

        onOrgDefaultChange(item, newRole) {
            const s = this.organizationSettings || {};
            const payload = {
                receivablesAccountDefaultId:
                    s.receivablesAccountDefaultId != null ? s.receivablesAccountDefaultId : null,
                incomeAccountDefaultId:
                    s.incomeAccountDefaultId != null ? s.incomeAccountDefaultId : null,
                vatAccountDefaultId: s.vatAccountDefaultId != null ? s.vatAccountDefaultId : null,
                depositAccountDefaultId:
                    s.depositAccountDefaultId != null ? s.depositAccountDefaultId : null,
            };

            const keys = ['receivablesAccountDefaultId', 'incomeAccountDefaultId', 'vatAccountDefaultId', 'depositAccountDefaultId'];
            keys.forEach((k) => {
                if (payload[k] === item.id) {
                    payload[k] = null;
                }
            });

            const roleToKey = {
                receivables: 'receivablesAccountDefaultId',
                income: 'incomeAccountDefaultId',
                vat: 'vatAccountDefaultId',
                deposit: 'depositAccountDefaultId',
            };

            if (newRole && roleToKey[newRole]) {
                payload[roleToKey[newRole]] = item.id;
            }

            this.savingOrgDefault = true;
            const body = organizationSettingsTransformer.sendAccountingAccountDefaults(payload);

            organizationSettingsService.updateAccounting(body)
                .then(() => {
                    this.savingOrgDefault = false;
                    this.$message({
                        showClose: true,
                        message: this.$t('static.organizationSettings.settingsSaveSuccess'),
                    });
                })
                .catch((errors) => {
                    this.savingOrgDefault = false;
                    let message = this.$t('static.organizationSettings.settingsSaveError');
                    if (errors && typeof errors === 'object') {
                        const first = Object.keys(errors)[0];
                        if (first && errors[first] && errors[first][0]) {
                            message = errors[first][0];
                        }
                    }
                    this.$message({
                        showClose: true,
                        customClass: 'error',
                        message,
                    });
                });
        },

        /**
         * Create new chart of accounts
         */

        createCoa() {
            this.ready = false;
            chartOfAccountsService.createCoa()
                .then((data) => {
                    this.ready = true;
                })
                .catch((errors) => {
                    this.ready = true;
                });
        },

        /**
         * Saves the form, adding an account.
         */
        save() {
            this.form.ready = false;

            const data = this.form.data();

            return new Promise((resolve, reject) => {
                chartOfAccountsService.store(data)
                    .then(() => {
                        chartOfAccountsService.all();
                        this.form.ready = true;
                        store.dispatch('addChartOfAccountsItem', data);
                        resolve();
                    }).catch((errors) => {
                    this.form.ready = true;
                    let message = '';
                    if (errors.code) {
                        message = errors.message;
                    } else {
                        this.form.recordErrors(errors);
                        const errorsKeys = Object.keys(this.form.errors.errors);
                        message = errors[errorsKeys[0]][0];
                    }
                    this.$message({
                        showClose: true,
                        customClass: 'error',
                        message,
                    });
                    reject();
                });
            });
        },

        /**
         * Updates an account
         */
        update(id) {
            this.form.ready = false;
            const data = this.form.data();

            return new Promise((resolve, reject) => {
                chartOfAccountsService.update(id, data)
                    .then(() => {
                        chartOfAccountsService.all();
                        this.form.ready = true;
                        store.dispatch('updateChartOfAccountsItem', { data: data, id: id});
                        resolve();
                    }).catch((errors) => {
                    this.form.ready = true;
                    this.form.recordErrors(errors);
                    const errorsKeys = Object.keys(this.form.errors.errors);
                    this.$message({
                        showClose: true,
                        message: errors[errorsKeys[0]][0],
                        customClass: 'error',
                    });
                    reject();
                });
            });
        },

        /**
         * Delete the account from chart of accounts.
         */
        destroy(id) {
            return new Promise((resolve, reject) => {
                chartOfAccountsService.destroy(id)
                    .then(() => {
                        organizationSettingsService.show();
                        resolve();
                    }).catch(() => {
                    reject();
                });
            });
        },

        getResources() {
            this.ready = false;
            Promise.all([
                chartOfAccountsService.all(),
                organizationSettingsService.show(),
            ]).then(() => {
                this.ready = true;
            }).catch((error) => {
                this.ready = true;
            });
        },
    },

    mounted() {
        this.getResources();
    },

    components: {
        //VTable: require('./../table/table.vue'),
        VFormTable: require('./../formTable/formTable.vue'),
        VMessage: require('./../form/message/message.vue'),
    },
};



// WEBPACK FOOTER //
// ./src/app/components/chartOfAccounts/chartOfAccounts.js